// src/components/VideoGalleryCard.tsx
"use client";

import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useSession } from 'next-auth/react';
import { Play, Lock, Heart } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { maybeCacheBust, API_BASE, toAbsoluteMediaUrl } from '@/lib/media';
import { scrubContactInfo } from '@/lib/scrubContact';
import AudienceBadge from '@/components/content/AudienceBadge';
import { isPrivateAudience } from '@/lib/audience';
import { useAutoplayPreview } from '@/components/video/useAutoplayPreview';
// NO CLIENT-SIDE GATING — the API returns the correct thumbnail URL per viewer.
// See: docs/CONTENT-GATING-SPEC.md.
export interface VideoGalleryCardProps {
  id: number;
  title: string;
  thumbnailUrl: string;
  viewCount?: number | null;
  durationSeconds?: number | null;
  unsafe?: boolean;
  /**
   * Who may be delivered this video ('Public' | 'Friends' | 'Watchers').
   * Presentation only: a row that reaches this card is already in-audience
   * server-side; we just label the exclusivity for the viewer inside it.
   */
  audience?: string;
  uploader?: {
    displayName: string;
    avatarUrl?: string;
    badge?: string;
    profileUrl?: string;
  };
  /** When false, hides the duration overlay badge. Defaults to true. */
  showDurationOverlay?: boolean;
  /** surface treatment */
  surface?: 'default' | 'light';
  /**
   * Whether the signed-in viewer has already liked this video, as the API
   * reports it. Omit it (as the owner's own video grid does) and the card
   * renders no like control at all.
   *
   * No like COUNT is rendered at any value, deliberately. A floor would still
   * publish the number the moment it is crossed, which is the same
   * anti-social-proof problem view counts were removed from this page for on
   * 2026-07-30, only deferred. Received likes belong on the creator's own
   * surface, not on a public browse card.
   */
  isLikedByMe?: boolean;
  /**
   * HLS manifest for the muted in-view preview. The API returns null for
   * guests (no blurred-video variant exists), so a guest card never has one
   * to play. Omit it and the card stays a still, which is what every surface
   * other than /videos does.
   */
  contentUrl?: string | null;
}

const CDN_BASE = process.env.NEXT_PUBLIC_CDN_BASE || '';

export default function VideoGalleryCard({ id, title, thumbnailUrl, viewCount, durationSeconds, unsafe, audience, uploader, showDurationOverlay = true, surface = 'default', isLikedByMe, contentUrl }: VideoGalleryCardProps) {
  const { data: session, status } = useSession();
  // Guests get no control rather than a dead one: the endpoint is [Authorize]
  // and a guest press could only ever produce a 401. The API also reports
  // isLikedByMe as false (not absent) for guests, so the session is the only
  // honest signal here.
  const canLike = status === 'authenticated' && typeof isLikedByMe === 'boolean';
  const [liked, setLiked] = React.useState<boolean>(!!isLikedByMe);
  const [likeBusy, setLikeBusy] = React.useState(false);

  // Preview playback. Gated on the session as well as on contentUrl: the API
  // already withholds the manifest from guests, and the card does not rely on
  // that alone.
  const { containerRef, videoRef, showPreview } = useAutoplayPreview({
    id,
    contentUrl,
    enabled: status === 'authenticated',
    session,
  });

  async function toggleLike() {
    if (likeBusy || !canLike) return;
    const prev = liked;
    setLiked(!prev);
    setLikeBusy(true);
    try {
      // Called directly rather than through lib/engagement's toggleLike, which
      // reports a refusal as success: on a 400 it falls back to `!currentLiked`,
      // so liking your own video from this grid would leave a filled heart the
      // server never recorded. Here a non-OK answer reverts, which covers
      // cannot_like_own_content and any audience or moderation refusal.
      const init: RequestInit = {
        method: prev ? 'DELETE' : 'POST',
        cache: 'no-store',
        body: JSON.stringify({ type: 'video', id: String(id) }),
      };
      // apiFetch treats its 2nd argument as the init unless it looks like a
      // session, so passing a null session with a 3rd argument would silently
      // DROP this init and send a GET, which answers 200 and would leave the
      // heart filled having recorded nothing. Pick the overload explicitly.
      const res = session
        ? await apiFetch('/api/engagement/like', session, init)
        : await apiFetch('/api/engagement/like', init);
      if (!res.ok) setLiked(prev);
    } catch {
      setLiked(prev);
    } finally {
      setLikeBusy(false);
    }
  }

  // Be tolerant to alternate keys and build absolute URL candidates
  const thumbKey = (thumbnailUrl as any) || (uploader as any)?.thumbnailUrl || (uploader as any)?.thumbnail || '';
  const [imgFailed, setImgFailed] = React.useState(false);
  // Build CDN URL - API sends correct blurred URLs for guests
  const imgSrc = React.useMemo(() => {
    const raw = String(thumbKey || '').trim();
    if (!raw) return '';

    if (/^https?:\/\//i.test(raw)) return maybeCacheBust(raw);
    const cleaned = raw.replace(/^\/+/, '');
    return maybeCacheBust(toAbsoluteMediaUrl(cleaned));
  }, [thumbKey]);
  // Guest-gated media: the server sent the _blurred variant. Label it so the
  // blur reads as a membership perk, not a broken thumbnail — same signal and
  // pill as TalentCard (re-score 07-11). Presentation only; gating is server-side.
  const isBlurred = /_blurred/i.test(imgSrc);
  // no action bar on gallery cards

  // Token-based card with border + glow, aligned with Talent/Post/Image cards
  return (
    <article className="w-full bg-card text-card-foreground border border-zinc-200/60 dark:border-zinc-800 rounded-2xl shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden">
      <Link href={`/video/${String(id)}`} prefetch={false} className="block">
        <div ref={containerRef} className="relative w-full aspect-[16/9]">
          {showPreview && (
            // Layered OVER the thumbnail inside the fixed-ratio box, so the
            // poster stays painted underneath and starting or stopping a
            // preview moves nothing. Muted and playsInline are what make
            // autoplay permissible at all; controls are deliberately absent
            // because the card is a link to the real player.
            <video
              ref={videoRef}
              // pointer-events-none so the whole tile stays one link target:
              // the preview is decoration, the click belongs to the card.
              className="pointer-events-none absolute inset-0 z-10 h-full w-full rounded-t-[inherit] object-cover"
              muted
              playsInline
              loop
              preload="none"
              poster={imgSrc || undefined}
              tabIndex={-1}
              aria-hidden
            />
          )}
          {imgSrc && !imgFailed ? (
            <Image
              src={imgSrc}
              alt={title ? `Video thumbnail: ${title}` : 'Video thumbnail'}
              fill
              sizes="(min-width: 1536px) 33vw, (min-width: 1024px) 45vw, 90vw"
              className="object-cover rounded-t-[inherit]"
              onError={() => setImgFailed(true)}
              priority={false}
            />
          ) : (
            <div className="absolute inset-0 grid place-items-center bg-muted rounded-t-[inherit]">
              <span className="text-xs text-muted-foreground">No thumbnail</span>
            </div>
          )}
          {imgSrc && !imgFailed && (
            // Play affordance so a gated/blurred thumbnail still reads as a
            // video rather than a broken image (re-score 07-11).
            <span className="pointer-events-none absolute inset-0 grid place-items-center">
              <span className="grid h-11 w-11 place-items-center rounded-full bg-black/45 text-white ring-1 ring-white/25 backdrop-blur-sm">
                <Play className="h-5 w-5 translate-x-[1px]" fill="currentColor" strokeWidth={0} aria-hidden />
              </span>
            </span>
          )}
          {isBlurred && (
            <span className="absolute top-2 right-2 z-20 inline-flex items-center gap-1 rounded-full bg-black/55 backdrop-blur-sm px-2 py-0.5 text-[11px] font-medium text-white">
              <Lock className="h-3 w-3" aria-hidden="true" />
              <span>Join free to view</span>
            </span>
          )}
          {isPrivateAudience(audience) && (
            // Top-LEFT so it never overlaps the Lock pill (top-right). Same
            // dark overlay treatment as that pill; the badge's own chip
            // colors are overridden via className (twMerge).
            <span className="absolute top-2 left-2 z-20">
              <AudienceBadge
                audience={audience}
                className="border-transparent bg-black/55 backdrop-blur-sm text-[11px] text-white"
              />
            </span>
          )}
          {showDurationOverlay && typeof durationSeconds === 'number' && durationSeconds > 0 && (
            <div className="absolute right-2 bottom-2 text-[11px] font-medium bg-black/70 text-white rounded px-1.5 py-0.5">
              {(() => {
                const total = Math.max(0, Math.floor(durationSeconds as number));
                const mm = Math.floor(total / 60);
                const ss = total % 60;
                const mmss = `${mm}:${ss.toString().padStart(2, '0')}`;
                return <span aria-label={`Duration ${mmss}`}>{mmss}</span>;
              })()}
            </div>
          )}
        </div>
      </Link>
      <div className="p-5 min-h-[96px] flex flex-col justify-between">
        <Link href={`/video/${String(id)}`} prefetch={false}>
          {/* h3 to match PostCard: section titles are h2 (SectionHeader). */}
          <h3 className="mb-2 font-serif text-lg font-semibold tracking-tight leading-tight text-foreground line-clamp-2">
            {(title && title.trim()) ? scrubContactInfo(title) : 'Untitled video'}
          </h3>
        </Link>
        {/* The views line already reserves this row's height with a space when
            counts are hidden, so the like control sits opposite it and its
            absence (guests, or the owner's own grid) shifts nothing. */}
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm text-muted-foreground">
            {typeof viewCount === 'number' ? `${viewCount.toLocaleString()} views` : ' '}
          </p>
          {canLike && (
            <button
              type="button"
              onClick={toggleLike}
              disabled={likeBusy}
              aria-pressed={liked}
              aria-label={liked ? 'Unlike this video' : 'Like this video'}
              title={liked ? 'Unlike' : 'Like'}
              className="-mr-1 grid h-9 w-9 shrink-0 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-accent hover:text-pink-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-500/50 disabled:opacity-50"
            >
              {/* Stroke to fill on state change, matching LikeShareReport. */}
              <Heart className={`h-[18px] w-[18px] ${liked ? 'fill-current text-pink-500' : ''}`} aria-hidden />
            </button>
          )}
        </div>
      </div>
    </article>
  );
}
